import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, ArrowDownToLine, Search, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full px-3 py-2 border-2 border-[#3D2008] bg-white font-manrope text-sm text-[#3D2008] focus:outline-none focus:border-[#C4522A] transition-colors";
const labelCls = "font-manrope text-xs font-bold text-[#3D2008] uppercase tracking-wide block mb-1";
const btnCls = "flex items-center gap-2 px-5 py-2.5 font-manrope font-semibold text-sm border-2 border-[#3D2008] shadow-[3px_3px_0px_0px_rgba(61,32,8,0.3)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-transform disabled:opacity-60 disabled:cursor-not-allowed";

const CountTable = ({ rows, cols, testId }) => (
    <div className="overflow-x-auto border-2 border-[#3D2008] bg-white" data-testid={testId}>
        <table className="w-full border-collapse text-sm">
            <thead><tr className="bg-[#3D2008] text-[#F5EDD6]">
                {["", ...cols.map(c => c.label)].map((c) => (
                    <th key={c || "name"} className="text-left px-4 py-2.5 font-manrope font-bold uppercase text-xs tracking-wide whitespace-nowrap">{c}</th>
                ))}
            </tr></thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={r.key} className={i % 2 === 0 ? "bg-white" : "bg-[#F5EDD6]/60"} data-testid={`${testId}-row-${r.key}`}>
                        <td className="px-4 py-2.5 font-manrope font-semibold text-[#3D2008] border-b border-gray-100">{r.label}</td>
                        {cols.map(c => (
                            <td key={c.field} className="px-4 py-2.5 font-manrope text-[#3D2008] border-b border-gray-100">
                                {r[c.field] === null || r[c.field] === undefined ? <span className="text-gray-400">not available</span> : r[c.field]}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

export const DataTransferTab = ({ adminKey, onImported }) => {
    const [url, setUrl] = useState("https://seeleyskatepark.org");
    const [pw, setPw] = useState("");
    const [replace, setReplace] = useState(true);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState("");
    const h = { "x-admin-password": adminKey };

    const run = async (dryRun) => {
        if (!url.trim() || !pw) { toast.error("Enter the old site's address and its admin password."); return; }
        if (!dryRun && !window.confirm(`Import everything from ${url} into this site?${replace ? "\n\nEvents, sponsors, products, photos and documents on THIS site that don't exist on the old site will be removed." : ""}`)) return;
        setBusy(dryRun ? "check" : "import");
        try {
            const r = await axios.post(`${API}/admin/import-from-site`,
                { source_url: url, source_password: pw, dry_run: dryRun, replace_content: replace }, { headers: h });
            if (dryRun) { setPreview(r.data); setResult(null); }
            else { setResult(r.data); toast.success("Import complete — the old site's info is now on this site."); if (onImported) onImported(); }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Something went wrong. Please try again.");
        } finally { setBusy(""); }
    };

    const exportBackup = async () => {
        setBusy("export");
        try {
            const r = await axios.get(`${API}/admin/export`, { headers: h, responseType: "blob" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(r.data);
            a.download = `slsa-backup-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(a.href);
            toast.success("Backup downloaded.");
        } catch { toast.error("Backup failed."); }
        finally { setBusy(""); }
    };

    return (
        <div data-testid="transfer-tab" className="max-w-3xl">
            <h2 className="font-anton text-2xl text-[#3D2008] uppercase mb-2">Data Transfer</h2>
            <p className="font-manrope text-sm text-[#5C4A35] mb-6 max-w-2xl">
                Roll everything over from the old site — events, sponsors, store products, photos, documents, donations, contact messages, email list, volunteers and site settings.
            </p>

            <div className="bg-[#F5EDD6] border-2 border-[#3D2008] p-6 mb-6">
                <h3 className="font-anton text-lg text-[#3D2008] uppercase mb-4">Import from the old site</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className={labelCls}>Old site address</label>
                        <input type="url" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://seeleyskatepark.org" data-testid="transfer-source-url" className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}>Old site admin password</label>
                        <input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="••••••••" data-testid="transfer-source-password" className={inputCls} />
                    </div>
                </div>
                <label className="flex items-start gap-3 mb-5 cursor-pointer">
                    <input type="checkbox" checked={replace} onChange={e => setReplace(e.target.checked)} data-testid="transfer-replace-checkbox" className="mt-0.5 w-4 h-4 accent-[#C4522A]" />
                    <span className="font-manrope text-sm text-[#3D2008]">
                        Make this site match the old site — remove events, sponsors, products, photos and documents that only exist here.
                        <span className="block text-xs text-[#5C4A35] mt-0.5">Donations, contacts, email list and volunteers are always kept and merged, never deleted.</span>
                    </span>
                </label>
                <div className="flex flex-wrap gap-3">
                    <button onClick={() => run(true)} disabled={!!busy} data-testid="transfer-check-btn" className={`${btnCls} bg-white text-[#3D2008]`}>
                        <Search size={15} /> {busy === "check" ? "Checking…" : "1. Check old site"}
                    </button>
                    <button onClick={() => run(false)} disabled={!!busy || !preview} data-testid="transfer-import-btn" className={`${btnCls} bg-[#C4522A] text-white`}>
                        <ArrowDownToLine size={15} /> {busy === "import" ? "Importing… this can take a minute" : "2. Import everything"}
                    </button>
                </div>

                {preview && !result && (
                    <div className="mt-6">
                        <p className="font-manrope text-sm text-[#3D2008] mb-3">Found on <span className="font-semibold">{preview.source_url}</span>. Compare, then click <span className="font-semibold">Import everything</span>.</p>
                        <CountTable rows={preview.collections} testId="transfer-preview-table"
                            cols={[{ field: "source", label: "Old site" }, { field: "local", label: "This site" }]} />
                    </div>
                )}

                {result && (
                    <div className="mt-6" data-testid="transfer-result">
                        <p className="flex items-center gap-2 font-manrope text-sm font-semibold text-[#2D5A27] mb-3"><CheckCircle2 size={16} /> Import complete.</p>
                        <CountTable rows={result.collections} testId="transfer-result-table"
                            cols={[{ field: "imported", label: "Imported" }, { field: "removed", label: "Removed here" }]} />
                        {result.skipped?.length > 0 && (
                            <p className="font-manrope text-xs text-[#5C4A35] mt-3">Not available on the old site (left unchanged here): {result.skipped.join(", ")}.</p>
                        )}
                    </div>
                )}
            </div>

            <div className="bg-[#F5EDD6] border-2 border-[#3D2008] p-6">
                <h3 className="font-anton text-lg text-[#3D2008] uppercase mb-1">Download a backup</h3>
                <p className="font-manrope text-sm text-[#5C4A35] mb-4">Saves every record on this site as one file you can keep for safekeeping.</p>
                <button onClick={exportBackup} disabled={!!busy} data-testid="transfer-export-btn" className={`${btnCls} bg-[#2D5A27] text-white`}>
                    <Download size={15} /> {busy === "export" ? "Preparing…" : "Download backup (.json)"}
                </button>
            </div>
        </div>
    );
};
